import { useCallback, useMemo } from "react";
import appLogger from "../../../infra/logger/appLogger";
import { LocalStorageKey, localStorageService } from "../../../services/index";
import { AnalyticsEvents, useAnalytics } from "../../analytics";
import { useAppDispatch, useAppSelector } from "../../hook";
import useUserInfo from "../useUserInfo";
import { DEFAULT_CHAT_LANGUAGE, languages } from "./constants";
import { blockCreator, CHARACTER_RATINGS_KEY, LASTTIME_FILTERED_CHARACTERS_KEY, likeBot, likeLorebook, setAutoResponse, setBlurNSFW, setCharacterRating, setCharactersRatings, setNSFWFilterEnabled, setResetUserSettings, setSelectedTags, setShowNSFW, setShowUnsavedProfileChangesWarning, setUserAvatarGenerationTime, setUserFirstTimeOnboardBannerVisible, setUserFirstTimeWaitingModalVisible, setUserLastTimeFilteredCharacters, setUserRememberSelectedPersonaModal, unblockCreator, updateAutoMemory, updateAutoResponse, updateBlurNsfw, updateChatLanguage, updateNewProfile, updateNSFWFilterEnabled, updateNSFWSettings, updateSelectedChatSizes, updateSelectedDefinitions, updateSelectedLanguages, updateSelectedTags, updateShowNsfw, updateTtsIncludeNarration, } from "./userSettingsSlice";
const userSettingsLogger = appLogger.withTag("UserSettings");
export default function useUserSettings() {
    const dispatch = useAppDispatch();
    const { userId, isAuthenticated } = useUserInfo();
    const { logEvent } = useAnalytics();
    const areUserSettingsLoaded = useAppSelector((state) => state.userSettings.are_user_settings_loaded);
    const blockedUsers = useAppSelector((state) => state.userSettings.blocked_users);
    const likedBots = useAppSelector((state) => state.userSettings.liked_bots);
    const likedLorebooks = useAppSelector((state) => state.userSettings.liked_lorebooks);
    const selectedTags = useAppSelector((state) => state.userSettings.selected_tags);
    const selectedLanguages = useAppSelector((state) => state.userSettings.selected_languages);
    const selectedChatSizes = useAppSelector((state) => state.userSettings.selected_chat_sizes);
    const selectedDefinitions = useAppSelector((state) => state.userSettings.selected_definitions);
    const blurNSFW = useAppSelector((state) => state.userSettings.blur_nsfw);
    const showNSFW = useAppSelector((state) => state.userSettings.show_nsfw);
    const isNewProfile = useAppSelector((state) => state.userSettings.new_profile);
    const showUnsavedProfileChangesWarning = useAppSelector((state) => state?.userSettings?.show_unsaved_profile_changes_warning);
    const isLoading = useAppSelector((state) => state.userSettings.is_loading);
    const isNSFWFilterEnabled = useAppSelector((state) => state.userSettings?.nsfw_filter_enabled);
    const ttsIncludeNarration = useAppSelector((state) => state.userSettings?.tts_include_narration);
    const chatLanguage = useAppSelector((state) => state.userSettings?.chat_language);
    const characterRatings = useAppSelector((state) => state.userSettings.character_ratings);
    const rememberSelectedPersonaModal = useAppSelector((state) => state.userSettings.remember_selected_persona_modal);
    const firstTimeOnboardBannerVisible = useAppSelector((state) => state.userSettings.first_time_onboard_banner_visible);
    const firstTimeWaitingModalVisible = useAppSelector((state) => state.userSettings.first_time_waiting_modal_visible);
    let lastTimeFilteredCharacters = useAppSelector((state) => state.userSettings.last_time_filtered_characters);
    const areUserSettingLoading = useAppSelector((state) => state.userSettings.are_user_settings_loading);
    const avatarGenerationTime = useAppSelector((state) => state.userSettings.avatar_generation_time);
    const isAutoMemoryEnabled = useAppSelector((state) => state.userSettings.isAutoMemoryEnabled);
    const autoResponse = useAppSelector((state) => state.userSettings.auto_response_enabled);
    const isChatLanguageAvailable = !isAuthenticated || areUserSettingsLoaded;
    const userChatLanguage = chatLanguage?.toLowerCase() || DEFAULT_CHAT_LANGUAGE;
    const userChatLanguageName = useMemo(() => languages.find((lang) => lang.code === userChatLanguage)?.name || userChatLanguage, [userChatLanguage]);
    const getCharacterRatingById = (characterId) => {
        const rating = characterRatings[characterId];
        return rating ?? null;
    };
    const getLastTimeFilteredCharacters = async () => {
        if (lastTimeFilteredCharacters === undefined) {
            const storeVal = await localStorageService.getItem(LASTTIME_FILTERED_CHARACTERS_KEY);
            lastTimeFilteredCharacters = storeVal ? Number.parseInt(storeVal) : undefined;
        }
        return lastTimeFilteredCharacters;
    };
    // The following functions are wrappers around the dispatch functions
    const onUserSelectsTag = useCallback((payload) => {
        return dispatch(updateSelectedTags(payload)).unwrap();
    }, [setSelectedTags]);
    const onUserSelectsDefinitions = useCallback((payload) => {
        return dispatch(updateSelectedDefinitions(payload)).unwrap();
    }, [updateSelectedDefinitions]);
    const onUserSelectsLanguage = useCallback((payload) => {
        return dispatch(updateSelectedLanguages(payload)).unwrap();
    }, [updateSelectedLanguages]);
    const onUserSelectsChatSize = useCallback((payload) => {
        return dispatch(updateSelectedChatSizes(payload)).unwrap();
    }, [updateSelectedChatSizes]);
    const onUserSetBlurNSFW = useCallback((payload) => {
        return dispatch(updateBlurNsfw(payload)).unwrap();
    }, [setBlurNSFW]);
    const onUserSetAutoMemory = useCallback((payload) => {
        return dispatch(updateAutoMemory(payload)).unwrap();
    }, [dispatch]);
    const onUserSetAutoResponse = useCallback((payload) => {
        return dispatch(updateAutoResponse(payload)).unwrap();
    }, [setAutoResponse]);
    const onUserSetShowNSFW = useCallback((showNSFW, screenName) => {
        if (screenName) {
            logEvent(AnalyticsEvents.SWITCH_NSFW, {
                user_id: userId,
                enable_nsfw: showNSFW,
                page_name: screenName,
            });
        }
        if (!showNSFW) {
            localStorageService.setItem(LocalStorageKey.NSFW_TOGGLE, "false");
        }
        return dispatch(updateShowNsfw(showNSFW)).unwrap();
    }, [setShowNSFW, userId]);
    const onUserSetNSFWSettings = useCallback((showNSFW, screenName) => {
        if (screenName) {
            logEvent(AnalyticsEvents.SWITCH_NSFW, {
                user_id: userId,
                enable_nsfw: showNSFW,
                page_name: screenName,
            });
        }
        if (!showNSFW) {
            localStorageService.setItem(LocalStorageKey.NSFW_TOGGLE, "false");
        }
        // If user is disabling NSFW, also disable blurNSFW if it's enabled
        const blurSettings = !showNSFW && blurNSFW ? { blurNSFW: false } : {};
        return dispatch(updateNSFWSettings({ showNSFW, ...blurSettings })).unwrap();
    }, [userId, blurNSFW]);
    const onUserSetTtsIncludeNarration = useCallback((ttsIncludeNarration, screenName) => {
        if (screenName) {
            logEvent(AnalyticsEvents.SWITCH_TTS_NARRATION, {
                user_id: userId,
                enable_tts_narration: ttsIncludeNarration,
                page_name: screenName,
            });
        }
        return dispatch(updateTtsIncludeNarration(ttsIncludeNarration)).unwrap();
    }, [updateTtsIncludeNarration, userId]);
    const onUserSetChatLanguage = useCallback((chatLanguage, screenName) => {
        if (screenName) {
            logEvent(AnalyticsEvents.CHOOSE_CHAT_LANGUAGE, {
                user_id: userId,
                chat_language: chatLanguage,
                page_name: screenName,
            });
        }
        return dispatch(updateChatLanguage(chatLanguage)).unwrap();
    }, [updateChatLanguage, userId]);
    const onUserLikebot = useCallback((id, screenName) => {
        if (screenName) {
            const isAlreadyLiked = likedBots.includes(id);
            logEvent(AnalyticsEvents.CHATBOT_FAVORITE, {
                user_id: userId,
                chatbot_id: id,
                mark_favorite: !isAlreadyLiked,
                page_name: screenName,
            });
        }
        return dispatch(likeBot(id)).unwrap();
    }, [likeBot, userId, likedBots]);
    const onUserLikeLorebook = useCallback((id, screenName) => {
        if (screenName) {
            const isAlreadyLiked = likedLorebooks.includes(id);
            logEvent(AnalyticsEvents.LOREBOOK_FAVORITE, {
                user_id: userId,
                lorebook_id: id,
                mark_favorite: !isAlreadyLiked,
                page_name: screenName,
            });
        }
        return dispatch(likeLorebook(id)).unwrap();
    }, [likeLorebook, userId, likedLorebooks]);
    const onUserBlockCreator = useCallback((creatorId) => {
        return dispatch(blockCreator(creatorId)).unwrap();
    }, [blockCreator]);
    const onUserUnblockCreator = useCallback((creatorId) => {
        return dispatch(unblockCreator(creatorId)).unwrap();
    }, [unblockCreator]);
    const onUserSetCharacterRating = useCallback((payload) => {
        dispatch(setCharacterRating(payload));
    }, [setCharacterRating]);
    const loadCharactersRatings = useCallback(async () => {
        try {
            const storeValString = await localStorageService.getItem(CHARACTER_RATINGS_KEY);
            if (storeValString) {
                const storeVal = JSON.parse(storeValString);
                if (storeVal) {
                    dispatch(setCharactersRatings(storeVal));
                }
            }
        }
        catch (error) {
            userSettingsLogger.error("Error parsing characters ratings from localStorage", error);
        }
    }, [setCharactersRatings]);
    const onUserUpdateNewProfile = useCallback((payload) => {
        dispatch(updateNewProfile(payload));
    }, [updateNewProfile]);
    const setToShowUnsavedProfileChangesWarning = useCallback((payload) => {
        dispatch(setShowUnsavedProfileChangesWarning(payload));
    }, []);
    const setFirstTimeWaitingModalVisible = useCallback((payload) => {
        dispatch(setUserFirstTimeWaitingModalVisible(payload));
    }, [setUserFirstTimeWaitingModalVisible]);
    const setFirstTimeOnboardBannerVisible = useCallback((payload) => {
        dispatch(setUserFirstTimeOnboardBannerVisible(payload));
    }, [setUserFirstTimeOnboardBannerVisible]);
    const onUserSetRememberSelectedPersonaModal = useCallback((payload) => {
        dispatch(setUserRememberSelectedPersonaModal(payload));
    }, [setUserRememberSelectedPersonaModal]);
    const setLastTimeFilteredCharacters = useCallback((payload) => {
        dispatch(setUserLastTimeFilteredCharacters(payload));
    }, [setUserLastTimeFilteredCharacters]);
    const setAvatarGenerationTime = useCallback((payload) => {
        dispatch(setUserAvatarGenerationTime(payload));
    }, [setUserAvatarGenerationTime]);
    const onResetUserSettings = useCallback(() => {
        dispatch(setResetUserSettings());
    }, []);
    const onUserNSFWFilterEnabled = useCallback((payload) => {
        return dispatch(updateNSFWFilterEnabled(payload)).unwrap();
    }, [setNSFWFilterEnabled]);
    return {
        isAutoMemoryEnabled,
        autoResponse,
        blurNSFW,
        showNSFW,
        isLoading,
        likedBots,
        likedLorebooks,
        isNewProfile,
        selectedTags,
        selectedLanguages,
        blockedUsers,
        characterRatings,
        avatarGenerationTime,
        areUserSettingLoading,
        areUserSettingsLoaded,
        lastTimeFilteredCharacters,
        showUnsavedProfileChangesWarning,
        firstTimeWaitingModalVisible,
        rememberSelectedPersonaModal,
        firstTimeOnboardBannerVisible,
        ttsIncludeNarration,
        chatLanguage,
        onUserLikebot,
        onUserLikeLorebook,
        onUserSelectsTag,
        onUserSelectsLanguage,
        onUserSelectsChatSize,
        onUserSelectsDefinitions,
        onUserSetAutoMemory,
        onUserSetAutoResponse,
        onUserSetBlurNSFW,
        onUserSetShowNSFW,
        onUserSetNSFWSettings,
        onUserSetTtsIncludeNarration,
        onUserSetChatLanguage,
        onResetUserSettings,
        onUserBlockCreator,
        onUserUnblockCreator,
        getCharacterRatingById,
        onUserUpdateNewProfile,
        setAvatarGenerationTime,
        onUserSetCharacterRating,
        setLastTimeFilteredCharacters,
        getLastTimeFilteredCharacters,
        setToShowUnsavedProfileChangesWarning,
        setFirstTimeWaitingModalVisible,
        setFirstTimeOnboardBannerVisible,
        onUserSetRememberSelectedPersonaModal,
        onUserNSFWFilterEnabled,
        isNSFWFilterEnabled,
        isChatLanguageAvailable,
        userChatLanguage,
        userChatLanguageName,
        selectedChatSizes,
        selectedDefinitions,
        loadCharactersRatings,
    };
}
